import { useState } from "react";
import PropTypes from "prop-types";
import BoxControls from "../../../Common/Box/BoxControls";
import Figure from "../../../Common/Box/Figure";
import useAreaQuery from "../../../../hooks/useAreaQuery";
import { capitalizePhrase, getTooltip } from "../../../../utilities";
import YearChooser from "../YearChooser";

const number = 7;

const getSeriesColor = (name, theme) => {
  switch (name) {
    case "Abortion":
      return theme.palette.areas.abortion.methods.abortion;

    case "PAC":
      return theme.palette.areas.abortion.methods.pac;

    case "Sterilization":
      return theme.palette.areas.contraception.methods.sterilization;

    case "Implant":
      return theme.palette.areas.contraception.methods.implant;

    case "IUD":
      return theme.palette.areas.contraception.methods.iud;

    case "Injectable":
      return theme.palette.areas.contraception.methods.injectable;

    case "Pill":
      return theme.palette.areas.contraception.methods.pill;

    case "Condom":
      return theme.palette.areas.contraception.methods.condom;

    case "Other":
      return theme.palette.areas.contraception.methods.other;

    default:
      return "black";
  }
};

const options = (data, props) => (theme) => {
  const absolute = props.comparisonMode !== "relative";

  const series = data.map((method) => ({
    name: method.name,
    data: method.countries.map(({ users }) => users),
    color: getSeriesColor(method.name, theme),
  }));

  return {
    chart: {
      type: "column",
      width: 1000,
      height: 1000,
      inverted: true,
      backgroundColor: null,
    },
    xAxis: {
      categories: data[0].countries.map(({ name }) => name),
      title: undefined,
      labels: {
        autoRotation: undefined,
        useHTML: true,
        style: {
          color: theme.figure.axis.primary,
          fontSize: theme.figure.axis.fontSize,
        },
      },
    },
    yAxis: {
      title: undefined,
      reversedStacks: false,
      max: absolute ? null : 100,
      labels: {
        format: absolute ? undefined : "{value}%",
        style: {
          color: theme.figure.axis.secondary,
          fontSize: theme.figure.axis.fontSize,
        },
      },
    },
    plotOptions: {
      column: {
        stacking: absolute ? "normal" : "percent",
        borderWidth: 0,
        groupPadding: 0.1,
      },
    },
    legend: {
      align: "right",
      verticalAlign: "middle",
      layout: "vertical",
      reversed: false,
      itemStyle: {
        fontSize: theme.figure.legend.fontSize,
      },
    },
    tooltip: {
      backgroundColor: "white",
      useHTML: true,
      outside: true,
      formatter: function () {
        return getTooltip("Users", this.y, this.percentage);
      },
      style: {
        fontSize: "0.875rem",
        color: theme.palette.tooltip.primary,
      },
    },
    exporting: {
      chartOptions: {
        title: undefined,
        subtitle: undefined,
      },
      enabled: false,
    },
    series,
  };
};

const FpCcF7 = (props) => {
  const [selectedYear, setSelectedYear] = useState(2030);
  const [comparisonMode, setComparisonMode] = useState("relative");

  const { areaData: data } = useAreaQuery(
    `/custom/fp/cc/figures/7/${props.regionId}/${selectedYear}`
  );

  const handleYearChange = (year) => {
    setSelectedYear(year);
  };

  const handleComparisonModeToggle = () => {
    setComparisonMode((comparisonMode) =>
      comparisonMode === "absolute" ? "relative" : "absolute"
    );
  };

  return (
    <Figure
      id="FP_CC_f7"
      number={number.toString()}
      fileStem={`fp_cc_f${number}`}
      title="Future Contraceptive Method Mix"
      subtitle={`${capitalizePhrase(
        props.regionName
      )} by Country, ${selectedYear}`}
      controls={
        <BoxControls
          comparisionMode={comparisonMode}
          onToggleComparisonMode={handleComparisonModeToggle}
        >
          <YearChooser value={selectedYear} onChange={handleYearChange} />
        </BoxControls>
      }
      chartOptions={data && options(data, { comparisonMode })}
    />
  );
};

FpCcF7.propTypes = {
  regionName: PropTypes.string.isRequired,
};

export default FpCcF7;
